import { useMutation } from '@tanstack/react-query';
import { AlertCircle, CheckCircle, Mail } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../api/index';
import { Button } from '../components/Button';
import { validateEmail } from '../lib/validation';

export function VerifyEmailPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [showResendForm, setShowResendForm] = useState(false)
  const [resendEmail, setResendEmail] = useState('')
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error' | null>(null)
  const verificationTriggered = useRef(false)

  const verifyMutation = useMutation({
    mutationFn: () => authAPI.verifyEmail(token!),
    onSuccess: () => {
      setVerificationStatus('success')
      toast.success('Email verified successfully! Redirecting to login...')
      setTimeout(() => navigate('/login', { replace: true }), 2000)
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.error || 'Verification failed'
      setVerificationStatus('error')
      if (errorMsg.includes('already used')) {
        toast.error('This verification link has already been used')
      } else if (errorMsg.includes('expired')) {
        toast.error('This verification link has expired')
        setShowResendForm(true)
      } else {
        toast.error(errorMsg)
      }
    },
  })

  // Auto-verify on page load
  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true })
      return
    }
    
    // Only trigger verification once
    if (!verificationTriggered.current) {
      verificationTriggered.current = true
      setVerificationStatus('loading')
      verifyMutation.mutate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const resendMutation = useMutation({
    mutationFn: () => authAPI.resendVerificationEmail(resendEmail),
    onSuccess: () => {
      toast.success('Verification email sent! Please check your inbox.')
      setResendEmail('')
      setShowResendForm(false)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to resend email')
    },
  })

  const handleResend = (e: React.FormEvent) => {
    e.preventDefault()
    if (!resendEmail) {
      toast.error('Please enter your email address')
      return
    }

    if (!validateEmail(resendEmail)) {
      toast.error('Please enter a valid email address')
      return
    }

    resendMutation.mutate()
  }

  if (!token) {
    return null
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-navy text-white flex-col justify-center items-center p-12">
        <div className="max-w-md">
          <div className="text-5xl font-bold mb-6">RTO Agents</div>
          <p className="text-xl text-gray-300 mb-8">
            Manage your agency's vehicle documents with ease
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-saffron rounded-full flex items-center justify-center mt-1">
                ✓
              </div>
              <p className="text-gray-200">Complete document management system</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-saffron rounded-full flex items-center justify-center mt-1">
                ✓
              </div>
              <p className="text-gray-200">Real-time expiry tracking and alerts</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-saffron rounded-full flex items-center justify-center mt-1">
                ✓
              </div>
              <p className="text-gray-200">Team collaboration and permissions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - Verify form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 bg-white">
        <div className="max-w-md w-full">
          {/* Loading State */}
          {verificationStatus === 'loading' && (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="relative w-16 h-16">
                  <div className="absolute inset-0 bg-saffron rounded-full animate-pulse" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Mail className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Verifying Email</h1>
                <p className="text-gray-600">
                  Please wait while we verify your email address...
                </p>
              </div>
            </div>
          )}

          {/* Success State */}
          {verificationStatus === 'success' && (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-sage" />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Email Verified!</h1>
                <p className="text-gray-600">
                  Your email has been verified successfully. Redirecting to login...
                </p>
              </div>
            </div>
          )}

          {/* Error State - Expired/Used Link */}
          {verificationStatus === 'error' && showResendForm && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Link Expired</h1>
                <p className="text-gray-600 mb-6">
                  Your verification link has expired. Please request a new one.
                </p>
              </div>

              <form onSubmit={handleResend} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                      disabled={resendMutation.isPending}
                    />
                  </div>
                  {resendEmail && !validateEmail(resendEmail) && (
                    <p className="mt-1 text-xs text-red-600">Invalid email address.</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={resendMutation.isPending}
                  className="w-full"
                >
                  {resendMutation.isPending ? 'Sending...' : 'Resend Verification Email'}
                </Button>
              </form>
            </div>
          )}

          {/* Error State - Other Errors */}
          {verificationStatus === 'error' && !showResendForm && (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Verification Failed</h1>
                <p className="text-gray-600 mb-6">
                  We encountered an issue verifying your email. Please try again or contact support.
                </p>
              </div>

              <Link to="/login">
                <Button variant="secondary" className="w-full">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          )}

          {/* Help Section */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-center text-sm text-gray-600">
              {verificationStatus === 'loading' ? (
                <>Already verified? <Link to="/login" className="text-saffron hover:text-saffron-dark font-medium">Sign in here</Link></>
              ) : (
                <>Need help? <Link to="/login" className="text-saffron hover:text-saffron-dark font-medium">Contact support</Link></>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
